import React, { useState } from 'react';
import { Box, Button, IconButton, InputAdornment, Popover, Tooltip, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { filterBadgeStyle } from '../MapHeader/MapHeaderSearchFilters.styles';
import { colors } from '../../../styles/theme';
import StageDot from '../../../Leads/components/StageDot';
import { DOCK_SCROLLBAR_SX } from '../DockPanel/Tabs/formBlockUi';
import {
  getLeadStageFilterChipSx,
  leadsSectionTitleSx,
  LEADS_TEXT_SECONDARY,
  leadsSummaryClearButtonSx,
} from '../../../Leads/components/leadsUi.styles';
import {
  COMMERCIAL_STATUS_FILTER_OPTIONS,
  DEFAULT_COMMERCIAL_FILTERS,
  STALE_DAYS_OPTIONS,
  countActiveCommercialFilters,
} from '../../../Leads/recordCommercialFilters';

const sectionTitleSx = { ...leadsSectionTitleSx, mb: 0.75 };
const groupSx = { display: 'flex', gap: 0.75, flexWrap: 'wrap' };

/**
 * Ícono de filtros dentro del buscador (mismo patrón que el de Leads: TuneIcon 28px + badge) + popover de filtros comerciales (mismo popover que Filtros de Leads).
 * Solo se monta donde el usuario gestiona (dueño / editor / invitado del espacio). Nunca en share.
 * @param {{ value: object, onChange: (next: object) => void, triggerSx?: object }} props
 */
export default function CommercialFiltersButton({ value = DEFAULT_COMMERCIAL_FILTERS, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const active = countActiveCommercialFilters(value);
  const set = (patch) => onChange?.({ ...value, ...patch });
  const toggleStatus = (s) => set({
    status: value.status.includes(s) ? value.status.filter((x) => x !== s) : [...value.status, s],
  });

  return (
    <>
      <Tooltip title="Filtros comerciales" placement="top" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Filtros comerciales"
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          sx={{
            position: 'relative',
            width: 28,
            height: 28,
            p: 0.25,
            borderRadius: '6px',
            color: anchor || active ? colors.primary : LEADS_TEXT_SECONDARY,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.06)',
              color: anchor || active ? colors.primary : colors.textPanelTitle,
            },
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          <TuneIcon />
          {active > 0 && (
            <span style={{ ...filterBadgeStyle, bottom: -3, right: -3 }} data-testid="commercial-filters-badge">
              {active}
            </span>
          )}
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              ...DOCK_SCROLLBAR_SX,
              mt: 0.75,
              width: 'min(360px, 92vw)',
              maxHeight: 'min(80vh, 560px)',
              overflowY: 'auto',
              bgcolor: colors.backgroundSurface,
              color: colors.textPrimary,
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '10px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Filtros comerciales</Typography>
          {active > 0 && (
            <Button
              size="small"
              onClick={() => onChange?.({ ...DEFAULT_COMMERCIAL_FILTERS, status: [] })}
              sx={leadsSummaryClearButtonSx}
              aria-label="Limpiar filtros comerciales"
            >
              Limpiar
            </Button>
          )}
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Etapa</Typography>
          <Box role="group" aria-label="Etapa" sx={groupSx}>
            {COMMERCIAL_STATUS_FILTER_OPTIONS.map((o) => {
              const on = value.status.includes(o.value);
              return (
                <Button
                  key={o.value}
                  size="small"
                  aria-pressed={on}
                  onClick={() => toggleStatus(o.value)}
                  sx={getLeadStageFilterChipSx(on, o.value)}
                >
                  {o.color ? <StageDot color={o.color} /> : null}
                  {o.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Seguimiento</Typography>
          <Box sx={groupSx}>
            <Button size="small" aria-pressed={value.withOffers} onClick={() => set({ withOffers: !value.withOffers })} sx={getLeadStageFilterChipSx(value.withOffers)}>
              Con ofertas
            </Button>
            <Button size="small" aria-pressed={value.unattended} onClick={() => set({ unattended: !value.unattended })} sx={getLeadStageFilterChipSx(value.unattended)}>
              Sin atender
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Sin movimiento hace más de</Typography>
          <Box role="group" aria-label="Sin movimiento" sx={groupSx}>
            {STALE_DAYS_OPTIONS.map((d) => (
              <Button
                key={d}
                size="small"
                aria-pressed={value.staleDays === d}
                onClick={() => set({ staleDays: value.staleDays === d ? null : d })}
                sx={getLeadStageFilterChipSx(value.staleDays === d)}
              >
                {`${d} días`}
              </Button>
            ))}
          </Box>
        </Box>

      </Popover>
    </>
  );
}

/**
 * `endAdornment` para el TextField de búsqueda (como Leads). Usar junto a
 * `commercialSearchFieldSx` para dejar el padding derecho del botón.
 */
export function CommercialFiltersAdornment(props) {
  return (
    <InputAdornment position="end" sx={{ ml: 0.25, flexShrink: 0 }}>
      <CommercialFiltersButton {...props} />
    </InputAdornment>
  );
}

/** Mismo ajuste que el buscador de Leads: conserva `listSearchFieldSx`, solo cambia el padding derecho. */
export const withCommercialAdornmentSx = (fieldSx) => ({
  ...fieldSx,
  '& .MuiOutlinedInput-root': {
    ...(fieldSx?.['& .MuiOutlinedInput-root'] || {}),
    pr: '6px',
  },
});
